import * as yup from 'yup'
import { useModal } from 'front-end/src/app/hooks/useModal'
import React, { useEffect, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import { useFormik } from 'formik'

import IconTrash from 'front-end/src/components/common/icon/IconTrash'
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Link,
  Modal,
  Spinner,
  ButtonOutline,
  ButtonPrimary,
  ButtonClean,
  ButtonDanger,
  ButtonIcon
} from 'front-end/src/components'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'

import {
  EmailIdentityFragment,
  useEditableSendersQuery,
  usePatchSenderMutation,
  useResendSenderMutation,
  useRevertSenderMutation
} from '../AccountPage.codegen'
import {
  notificationEmailAddress,
  notificationEmailName
} from '../../../../../shared/emails'
import { apolloClientPrivate } from '../../../graphql/apolloClient'
import { brandColors } from 'shared/basicPalette'
import { useCustomSmtpQuery } from './CustomSMTP.codegen'
import {
  RemoveEmailIdentityModal,
  RelatedCampaignsListWithAuthors
} from './RemoveEmailIdentityModal'
import {
  EmailIdentityRelatedCampaignsDocument,
  useArchiveSenderMutation
} from './RemoveEmailIdentityModal.codegen'

// used for keying unsaved identities (ignored by JSON.stringify)
const TEMP_KEY = Symbol()

const DEFAULT_SENDER = {
  id: null as any,
  name: notificationEmailName,
  email: notificationEmailAddress,
  order: 0
}

const useSenders = () => {
  const { featureFlags } = useFeatureFlags()

  const { data } = useEditableSendersQuery()
  const remoteSenders = data?.admin?.organisation?.emailIdentities

  const [patchSender] = usePatchSenderMutation()
  const [archiveSender] = useArchiveSenderMutation()
  const [resendSender] = useResendSenderMutation()
  const [revertSender] = useRevertSenderMutation({
    onCompleted: (res) => {
      const prev = res.admin?.organisation?.emailIdentity?.revert!
      setSenders(
        senders.map((s) =>
          // replace with prev
          s.previous_email_identity_id === prev.id ? prev : s
        )
      )
    }
  })

  let [senders, setSenders] = useState<EmailIdentityFragment[]>([])

  if (!senders.length) {
    senders = [DEFAULT_SENDER]
  }

  useEffect(() => setSenders(remoteSenders ?? []), [remoteSenders])

  return {
    senders,
    multipleSenderEnabled: featureFlags?.multiple_senders,

    keyOf: (sender) =>
      sender.id ?? sender[TEMP_KEY] ?? (sender[TEMP_KEY] = Date.now()),

    addEmptySender: () =>
      setSenders([
        ...senders,
        { id: null as any, name: '', email: '', order: null }
      ]),

    resendSender: async (sender) => {
      await resendSender({ variables: { id: sender.id } })
      notification.info(t`Verification email sent`)
    },

    revertSender: async (sender) => {
      await revertSender({ variables: { id: sender.id } })
      notification.info(t`Email sender reverted`)
    },

    saveSender: async (sender, data) => {
      const { id, name, email, order } = data
      const { data: result } = await patchSender({
        variables: { input: { id, name, email, order } }
      })
      const saved: EmailIdentityFragment =
        result!.admin!.organisation!.setEmailIdentity!

      if (!saved.verified_at) notification.info(t`Verification email sent`)

      // replace
      setSenders(senders.map((s) => (s === sender ? saved : s)))
    },

    removeSender: async (sender) => {
      if (sender.id) {
        await archiveSender({ variables: { id: sender.id } })
      }

      return setSenders(senders.filter((s) => s !== sender))
    }
  }
}

export const SenderDetails = () => {
  const {
    senders,
    multipleSenderEnabled,
    keyOf,
    addEmptySender,
    resendSender,
    revertSender,
    saveSender,
    removeSender
  } = useSenders()

  // number of currently-saved senders
  const savedCount = senders.filter((s) => s.id).length
  const { data } = useCustomSmtpQuery()

  const { open, currentModal } = useModal()

  const handleRemove = async (sender) => {
    if (!sender.id) {
      return removeSender(sender)
    }

    // show different modal depending on the number of saved senders
    open(savedCount === 1 ? RevertDefaultModal : RemoveEmailIdentityModal, {
      sender
    }).then(() => removeSender(sender))
  }

  const handleSave = async (data, sender, setLoadingHandler) => {
    let relatedCampaigns = []

    if (data.id) {
      setLoadingHandler(true) // Show spinner on Update/Save button
      const response = await apolloClientPrivate.query({
        query: EmailIdentityRelatedCampaignsDocument,
        variables: {
          id: data.id
        }
      })
      setLoadingHandler(false)

      relatedCampaigns =
        response.data?.admin?.organisation?.emailIdentity?.relatedCampaigns ??
        []
    }

    if (
      data.id &&
      sender.verified_at &&
      data.email === sender.email &&
      relatedCampaigns.length === 0
    ) {
      // Special use case when there is no need to show the modal. When this can happen:
      // Identity must be already verified AND there is no related campaign to this identity AND user changed just the name, not email.
      // If no campaigns are affected, no modal is necessary to be shown. We can directly save that Sender.
      await saveSender(sender, data)
      return
    }

    open(SaveModal, {
      sender,
      formData: data,
      senders,
      relatedCampaigns
    }).then(() => saveSender(sender, data))
  }

  const filterOutCustomSmtpIdentity = (
    emailIdentity: EmailIdentityFragment
  ): boolean => {
    // default email identity must not be edited the same way we do for all others
    return (
      emailIdentity.id !==
      data?.admin?.organisation?.custom_smtp?.emailIdentity?.id
    )
  }

  return (
    <div>
      <h4>
        <Trans>Sender details</Trans>{' '}
      </h4>
      <p className="text-description">
        <Trans>
          Give your users a better experience by adding an email address you
          would like notifications to be sent from. A verification email will be
          sent to this address to confirm you have access to the email account,
          and must be confirmed before we begin sending emails from this
          address.
        </Trans>
      </p>
      <Box py={3}>
        {senders.filter(filterOutCustomSmtpIdentity).map((sender, index) => (
          <SenderRow
            editable={
              // If multiple senders feature is disabled, user can edit only first record. Other records are frozen.
              multipleSenderEnabled || (!multipleSenderEnabled && index === 0)
            }
            key={keyOf(sender)}
            sender={sender}
            onResend={() => resendSender(sender)}
            onRevert={() => revertSender(sender)}
            onSave={async (setLoadingHandler, data) => {
              await handleSave(data, sender, setLoadingHandler)
            }}
            onRemove={() => handleRemove(sender)}
          />
        ))}
      </Box>
      {multipleSenderEnabled && (
        <ButtonOutline spacingDisabled onClick={addEmptySender}>
          <Trans>Add new sender</Trans>
        </ButtonOutline>
      )}
      {currentModal}
    </div>
  )
}

const SenderSchema = yup.object().shape({
  name: yup.string().required(),
  email: yup.string().email().required()
})

const SenderRow = ({
  sender,
  onResend,
  onRevert,
  onSave,
  onRemove,
  editable
}) => {
  const [loading, setLoading] = useState<boolean>(false)

  const formik = useFormik({
    initialValues: sender,
    enableReinitialize: true,
    validationSchema: SenderSchema,
    onSubmit: onSave.bind(null, setLoading)
  })

  const verificationStatus = !!sender?.verified_at ? (
    <FormHelperText color={brandColors.green.mid}>
      <span className="icon icon-correct" /> <Trans>Verified</Trans>
    </FormHelperText>
  ) : (
    <FormHelperText color={brandColors.red.mid}>
      <span className="icon icon-incorrect" />
      <Trans>Verification email sent. Not yet verified.</Trans>
      <Link color={brandColors.green.mid} ml="4px" onClick={onResend}>
        <Trans>Resend verification</Trans>
      </Link>
      {sender?.previous_email_identity_id && (
        <Link color={brandColors.green.mid} ml="4px" onClick={onRevert}>
          Revert to previous
        </Link>
      )}
    </FormHelperText>
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      <HStack alignItems="flex-start">
        <FormControl id={`sender-name-${sender.id}`}>
          <FormLabel>
            <Trans>Sender name</Trans>
          </FormLabel>
          <Input
            disabled={!editable}
            variant="filled"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
          />
        </FormControl>
        <FormControl id={`sender-email-${sender.id}`}>
          <FormLabel>
            <Trans>Sender address</Trans>
          </FormLabel>
          <HStack>
            <Input
              disabled={!editable}
              variant="filled"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
            />
            {formik.dirty && (
              <ButtonPrimary
                type="submit"
                disabled={!formik.isValid || loading || !editable}
              >
                {loading && <Spinner />} {sender.id ? t`Update` : t`Save`}
              </ButtonPrimary>
            )}
            {sender !== DEFAULT_SENDER && editable && (
              <ButtonIcon
                aria-label={t`Delete`}
                icon={<IconTrash />}
                onClick={(e) => {
                  onRemove()
                  // it's probably bug in chakra because type="button" doesn't work
                  // (but it does on their website)
                  e.preventDefault()
                }}
              />
            )}
          </HStack>

          {sender.id && !formik.dirty && verificationStatus}
        </FormControl>
      </HStack>
    </form>
  )
}

const SaveModal = ({
  sender,
  formData,
  senders,
  relatedCampaigns,
  resolve,
  reject
}) => {
  const isUsed = !!relatedCampaigns.length

  const isFirstEmailIdentity = !senders.find((sender) => sender.id)

  return (
    <Modal
      newDesign
      header={t`Save sender details`}
      controls={
        <>
          <ButtonClean onClick={reject}>
            <Trans>Cancel</Trans>
          </ButtonClean>
          <ButtonPrimary onClick={resolve}>
            <Trans>Save</Trans>
          </ButtonPrimary>
        </>
      }
      onHide={reject}
    >
      <>
        {isFirstEmailIdentity && (
          <p>
            <Trans>
              All your existing <strong>campaigns</strong> will use this email
              identity from now on.
            </Trans>
          </p>
        )}

        <p>
          <Trans>
            Using custom sender details can cause emails sent from the platform
            to be rejected.
          </Trans>
        </p>
        {(!sender || sender.email !== formData.email) && (
          <p>
            <Trans>
              To avoid this issue please check if there is{' '}
              <strong>Anti-Spoofing</strong> filter enabled on your{' '}
              <strong>SMTP</strong>. If it is please contact us before enabling
              Sender details and request to enable{' '}
              <strong>
                Domain-based Message Authentication, Reporting & Conformance
                (DMARC)
              </strong>{' '}
              policy to authenticate emails.
            </Trans>
          </p>
        )}

        {isUsed && (
          <RelatedCampaignsListWithAuthors campaigns={relatedCampaigns} />
        )}
      </>
    </Modal>
  )
}

const RevertDefaultModal = ({ resolve, reject }) => (
  <Modal
    header={t`Remove Sender and revert to system default`}
    controls={
      <>
        <ButtonClean onClick={reject}>
          <Trans>Cancel</Trans>
        </ButtonClean>
        <ButtonDanger onClick={resolve}>
          <Trans>Remove</Trans>
        </ButtonDanger>
      </>
    }
    onHide={reject}
  >
    <p>
      <Trans>
        Removing this sender will apply the default email sender{' '}
        <strong>{notificationEmailAddress}</strong> for all active campaigns.
      </Trans>
    </p>
    <p>
      <Trans>You can always re-add senders.</Trans>
    </p>
    <p>
      <Trans>
        If a sender address was verified, no further verification will be
        required.
      </Trans>
    </p>
  </Modal>
)
